import axios from 'axios';

const BASE_URL = process.env.REACT_APP_BASE_URL;

export const getUserById = async (userId, token) => {
  try {
    const response = await axios.get(`${BASE_URL}/user/v1/user/${userId}`, {
      headers: { 
        'Authorization': 'Bearer ' + token,
        'Content-Type': 'application/json'
      }
    });

    return {
      success: response.data.status === 0,
      user: response.data.data,
      message: response.data.message
    };
  } catch (error) {
    return handleError(error);
  }
};

export const updateUser = async (userData, token) => {
  try {
    const payload = {
      first_name: userData.firstName,
      last_name: userData.lastName,
      phone: userData.phone,
      company: userData.company,
      position: userData.position,
      status: userData.status
    };

    const response = await axios.patch(`${BASE_URL}/user/v1/user/${userData.id}`, payload, {
      headers: { 
        'Authorization': 'Bearer ' + token,
        'Content-Type': 'application/json'
      }
    });

    return {
      success: response.data.status === 0,
      user: response.data.data,
      message: response.data.message
    };
  } catch (error) {
    return handleError(error);
  }
};

export const deleteUser = async (userId, token) => {
  try {
    const response = await axios.delete(`${BASE_URL}/user/v1/user/${userId}`, {
      headers: { 
        'Authorization': 'Bearer ' + token,
        'Content-Type': 'application/json'
      }
    });

    return {
      success: response.data.status === 0,
      message: response.data.message
    };
  } catch (error) {
    return handleError(error);
  }
};

export const generateInvite = async (inviteData, token) => {
  try {
    const queryParams = new URLSearchParams();
    
    queryParams.append('email', inviteData.email);
    queryParams.append('groupId', inviteData.groupId);
    queryParams.append('type', inviteData.type);
    
    if (inviteData.type === 3) { // CLIENT
      queryParams.append('client_role', inviteData.role);
    } else if (inviteData.type === 2) { // DEPARTMENT
      queryParams.append('department_role', inviteData.role);
    } else if (inviteData.type === 1) { // TEAM
      queryParams.append('team_role', inviteData.role);
    }
    
    if (inviteData.baseUrl) {
      queryParams.append('base_url', inviteData.baseUrl + '/user');
    } else {
      const defaultBaseUrl = window.location.origin + '/user';
      queryParams.append('base_url', defaultBaseUrl);
    }
    

    const url = `${BASE_URL}/user/v1/user/invite?${queryParams.toString()}`;
    
    const response = await axios.get(url, {
      headers: { 
        'Authorization': 'Bearer ' + token,
        'Content-Type': 'application/json'
      }
    });
    

    return {
      success: response.data.status === 0,
      inviteLink: response.data.data,
      message: response.data.message
    };
  } catch (error) {
    return handleError(error);
  }
};

export const acceptInvite = async (userData) => {
  try {
    const params = {
      first_name: userData.firstName,
      last_name: userData.lastName,
      username: userData.email || userData.firstName.toLowerCase() + '.' + userData.lastName.toLowerCase(),
      password: userData.password,
      phone: userData.phone,
      company: userData.company,
      position: userData.position
    };
    
    const apiUrl = `${BASE_URL}/user/public/v1/user/invitation/${userData.token}`;
    
    const response = await axios.get(apiUrl, { params });

    return {
      success: response.data.status === 0,
      message: response.data.message
    };
  } catch (error) {
    return handleError(error);
  }
};

export const updateUserRole = async (userId, groupId, groupType, role, token, isSuperAdmin = false) => {
  try {
    const payload = {
      user_id: userId,
      group_id: groupId,
      type: groupType,
      super_admin: isSuperAdmin
    };

    // Set the appropriate role field based on group type
    if (groupType === 3) { // CLIENT
      payload.client_role = role;
    } else if (groupType === 2) { // DEPARTMENT
      payload.department_role = role;
    } else if (groupType === 1) { // TEAM
      payload.team_role = role;
    }

    const response = await axios.post(`${BASE_URL}/user/v1/user/role`, payload, {
      headers: { 
        'Authorization': 'Bearer ' + token,
        'Content-Type': 'application/json'
      }
    });

    return {
      success: response.data.status === 0,
      message: response.data.message
    };
  } catch (error) {
    return handleError(error);
  }
};

export const getCurrentUser = async (token) => {
  try {
    const response = await axios.get(`${BASE_URL}/user/v1/current/user`, {
      headers: { 
        'Authorization': 'Bearer ' + token,
        'Content-Type': 'application/json'
      }
    });

    return {
      success: response.data.status === 0,
      user: response.data.data,
      message: response.data.message
    };
  } catch (error) {
    return handleError(error);
  }
};

export const getUserCredentials = async (userId, token) => {
  try {
    const response = await axios.get(`${BASE_URL}/user/v1/user/creds/${userId}`, {
      headers: { 
        'Authorization': 'Bearer ' + token,
        'Content-Type': 'application/json'
      }
    });

    return {
      success: response.data.status === 0,
      credentials: response.data.data,
      message: response.data.message
    };
  } catch (error) {
    return handleError(error);
  }
};

export const getSuperUserCredentials = async (service, token) => {
  try {
    const response = await axios.get(`${BASE_URL}/user/v1/super-user/creds/${service}`, {
      headers: { 
        'Authorization': 'Bearer ' + token,
        'Content-Type': 'application/json'
      }
    });

    return {
      success: response.data.status === 0,
      credentials: response.data.data,
      message: response.data.message
    };
  } catch (error) {
    return handleError(error);
  }
};

export const getAllSuperUserCredentials = async (token) => {
  try {
    const response = await axios.get(`${BASE_URL}/user/v1/super-user/all`, {
      headers: { 
        'Authorization': 'Bearer ' + token,
        'Content-Type': 'application/json'
      }
    });

    return {
      success: response.data.status === 0,
      credentials: response.data.data,
      message: response.data.message
    };
  } catch (error) {
    return handleError(error);
  }
};

export const getUserGroups = async (userId, token) => {
  try {
    const response = await axios.get(`${BASE_URL}/user/v1/users/${userId}/groups`, {
      headers: { 
        'Authorization': 'Bearer ' + token,
        'Content-Type': 'application/json'
      }
    });

    return {
      success: response.data.status === 0,
      clients: response.data.clients || [],
      departments: response.data.departments || [],
      teams: response.data.teams || [],
      message: response.data.message
    };
  } catch (error) {
    return handleError(error);
  }
};

export const getAllUsers = async (params, token) => {
  try {
    const queryParams = new URLSearchParams();
    
    if (params.pagination) {
      queryParams.append('pagination.page', params.pagination.page || 1);
      queryParams.append('pagination.page_size', params.pagination.pageSize || 10);
    }
    
    if (params.sorting) {
      queryParams.append('sorting.sort_by', params.sorting.sort_by);
      queryParams.append('sorting.order', params.sorting.order);
    }
    
    if (params.query) {
      queryParams.append('query', params.query);
    }
    
    if (params.client_id) {
      queryParams.append('client_id', params.client_id);
    } else if (params.department_id) {
      queryParams.append('department_id', params.department_id);
    } else if (params.team_id) {
      queryParams.append('team_id', params.team_id);
    }
    
    if (params.enabled !== undefined) {
      queryParams.append('enabled', params.enabled);
    }
    
    const url = `${BASE_URL}/user/v1/users?${queryParams.toString()}`;
    console.log('User API URL:', url);
    
    const response = await axios.get(url, {
      headers: { 
        'Authorization': 'Bearer ' + token,
        'Content-Type': 'application/json'
      }
    });

    const responseData = response.data;
    console.log('User API response:', responseData);
    
    // Use camelCase keys for backward compatibility
    return {
      success: responseData.status === 0,
      users: responseData.data,
      totalCount: responseData.total_count || responseData.totalCount || 0,
      pageCount: responseData.page_count || responseData.pageCount || 0,
      page: responseData.page || 1,
      pageSize: responseData.page_size || responseData.pageSize || 10,
      message: responseData.message
    };
  } catch (error) {
    console.error('User API Error details:', error.response?.data || error.message);
    return handleError(error);
  }
};

export const removeUserFromDepartment = async (departmentId, userId, token) => {
  try {
    const response = await axios.delete(`${BASE_URL}/user/v1/department/${departmentId}/user/${userId}`, {
      headers: { 
        'Authorization': 'Bearer ' + token,
        'Content-Type': 'application/json'
      }
    });

    return {
      success: response.data.status === 0,
      message: response.data.message
    };
  } catch (error) {
    return handleError(error);
  }
};

export const removeUserFromTeam = async (teamId, userId, token) => {
  try {
    const response = await axios.delete(`${BASE_URL}/user/v1/team/${teamId}/user/${userId}`, {
      headers: { 
        'Authorization': 'Bearer ' + token,
        'Content-Type': 'application/json'
      }
    });

    return {
      success: response.data.status === 0,
      message: response.data.message
    };
  } catch (error) {
    return handleError(error);
  }
};

const handleError = (error) => {
  console.error('User API Error:', error);
  
  if (error.response?.status === 503) {
    return {
      success: false,
      error: 'Service temporarily unavailable'
    };
  }
  
  return {
    success: false,
    error: error.response?.data?.message || error.message || 'Operation failed'
  };
}; 

